import { EditorID } from 'Cloud/Application/Editor/types';
import { OLD_FORMAT_EXTS } from 'Cloud/ui/Viewer/viewer.constants';
import { type IExtInfo, extInfo } from 'lib/extInfo';
import { EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { canOpenSmallFile } from 'reactApp/modules/file/helpers/canOpenSmallFile';
import { getExtension } from 'reactApp/modules/file/helpers/getExtention';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { calculateMaxEditableOrPreviewableSizeForEditor } from 'server/helpers/editors/calculateMaxEditableOrPreviewableSizeForEditor';
import { sortEditors } from 'server/helpers/editors/sortEditors';
import type { ServerEditor } from 'server/helpers/editors/types';

import { getEditorsConfig } from './getEditorsConfig';

export const getAvailableEditorsFor = (props: {
    editors: ServerEditor[];
    item: { name: string; attachType?: EAttachTypes; size?: number; weblink?: string; ext?: string };
    isEdit: boolean;
    storage?: string | null;
    isWopiEnabled?: boolean;
    overrideExtInfoFeature?: IExtInfo;
}): ServerEditor[] => {
    const { item, editors, isEdit, isWopiEnabled, storage, overrideExtInfoFeature } = props;
    const { isStock = false, isAttaches = false } = storage ? getStorage(storage) : {};

    const ext = getExtension(item).toLowerCase();
    const extInf = Object.assign({}, extInfo.get(ext, isAttaches), overrideExtInfoFeature?.[ext]);

    return (
        editors
            // eslint-disable-next-line complexity
            .filter((editor) => {
                if (editor.id === EditorID.MYOFFICE && OLD_FORMAT_EXTS.includes(ext?.toLowerCase()) && !isWopiEnabled) {
                    return false;
                }

                if (!editor.tablet && EnvironmentSelectors.isIpadOs()) {
                    return false;
                }

                if (
                    (isStock || isAttaches || item.attachType) &&
                    (!editor.can_view_attach || (typeof editor.can_view_attach !== 'object' && editor.can_view_attach !== true))
                ) {
                    return false;
                }

                if (
                    typeof editor.can_view_attach === 'object' &&
                    (item.attachType || isAttaches || isStock) &&
                    ((isAttaches && !editor.can_view_attach.includes(item?.attachType || EAttachTypes.attach)) || // на странице атачей проверяем attachType
                        (isStock && !editor.can_view_attach.includes('stock')) || // на странице стоков проверяем stock
                        (!isAttaches && !isStock && item.attachType && !editor.can_view_attach.includes(item.attachType))) // если нету storage проверяем attachType
                ) {
                    return false;
                }

                const isAvailable = !editor.pages || !storage || editor.pages.includes(storage);

                if (!isAvailable) {
                    return false;
                }

                const size =
                    (isEdit
                        ? calculateMaxEditableOrPreviewableSizeForEditor(editor.id, extInf.maxEditableSize)
                        : !extInf.disablePreviewAsEditorOnPublic &&
                          calculateMaxEditableOrPreviewableSizeForEditor(editor.id, extInf.maxPreviewableSize)) || 0;

                const canOpenSmallSize = canOpenSmallFile(item);

                const isCalculatedSize = !item?.size || (canOpenSmallSize && item?.size <= size);

                if (!isCalculatedSize) {
                    return false;
                }

                if (!editor) {
                    return false;
                }

                const availableExtensions = isEdit ? editor.edit_extensions : editor.view_extensions;

                if (!availableExtensions?.includes(ext)) {
                    return false;
                }

                // stringifyEscapeScript убивает функции из конфигов, восстанавливаем при выборке
                editor.get_view_url = getEditorsConfig(editor.id)?.get_view_url;

                if ((isStock || isAttaches || item.attachType) && editor.attachOrder) {
                    editor.order = editor.attachOrder;
                }

                return true;
            })
            .sort(sortEditors)
    );
};
